/**
 * <s-hero-fade> — Gentle crossfade slideshow for the homepage hero.
 *
 * Markup (templates/index.liquid):
 *   <s-hero-fade interval="5000" data-slide-label="Imagen [n] de [total]" ...>
 *     <div data-hero-slide data-active> …image… </div>   (stacked; CSS fades opacity)
 *     <button data-hero-dot>…</button>                    (one per slide)
 *     <button data-hero-toggle data-label-pause data-label-play>…</button>
 *   </s-hero-fade>
 *
 * Behavior: advances every `interval` ms; pauses while hovered, while focus is inside, and
 * while the tab is hidden; the toggle button stops/starts it for good. With
 * prefers-reduced-motion it never auto-advances (dots still work, and the fade is instant via CSS).
 * Inactive slides get aria-hidden + inert; dots get aria-current.
 */
class SHeroFade extends HTMLElement {
  #slides = [];
  #dots = [];
  #toggle = null;
  #current = 0;
  #timer = null;
  #stopped = false;
  #hovered = false;
  #focused = false;
  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  #ac = null;

  connectedCallback() {
    this.#slides = Array.from(this.querySelectorAll('[data-hero-slide]'));
    this.#dots = Array.from(this.querySelectorAll('[data-hero-dot]'));
    this.#toggle = this.querySelector('[data-hero-toggle]');
    if (this.#slides.length < 2) {
      this.querySelector('[data-hero-controls]')?.setAttribute('hidden', '');
      return;
    }
    this.#ac = new AbortController();
    const { signal } = this.#ac;

    const total = this.#slides.length;
    const template = this.dataset.slideLabel || '[n] / [total]';
    this.#slides.forEach((slide, i) => {
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-roledescription', 'slide');
      slide.setAttribute('aria-label', template.replace('[n]', i + 1).replace('[total]', total));
    });
    this.#dots.forEach((dot, i) => dot.addEventListener('click', () => { this.show(i); this.#restart(); }, { signal }));
    this.#toggle?.addEventListener('click', () => {
      this.#stopped = !this.#stopped;
      this.#syncToggle();
      this.#restart();
    }, { signal });

    this.addEventListener('pointerenter', () => { this.#hovered = true; this.#restart(); }, { signal });
    this.addEventListener('pointerleave', () => { this.#hovered = false; this.#restart(); }, { signal });
    this.addEventListener('focusin', () => { this.#focused = true; this.#restart(); }, { signal });
    this.addEventListener('focusout', (e) => {
      if (!this.contains(e.relatedTarget)) { this.#focused = false; this.#restart(); }
    }, { signal });
    document.addEventListener('visibilitychange', () => this.#restart(), { signal });
    this.#reducedMotion.addEventListener('change', () => { this.#syncToggle(); this.#restart(); }, { signal });

    this.show(0);
    this.#syncToggle();
    this.#restart();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    clearInterval(this.#timer);
  }

  show(index) {
    const total = this.#slides.length;
    this.#current = (index + total) % total;
    this.#slides.forEach((slide, i) => {
      const active = i === this.#current;
      slide.toggleAttribute('data-active', active);
      slide.setAttribute('aria-hidden', String(!active));
      slide.toggleAttribute('inert', !active);
    });
    this.#dots.forEach((dot, i) => {
      if (i === this.#current) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }

  #playing() {
    return !this.#stopped && !this.#reducedMotion.matches && !this.#hovered && !this.#focused && !document.hidden;
  }

  #restart() {
    clearInterval(this.#timer);
    this.#timer = null;
    if (!this.#playing()) return;
    const interval = parseInt(this.getAttribute('interval'), 10) || 5000;
    this.#timer = setInterval(() => this.show(this.#current + 1), interval);
  }

  #syncToggle() {
    if (!this.#toggle) return;
    // Under reduced motion there's no autoplay, so the toggle is pointless.
    this.#toggle.hidden = this.#reducedMotion.matches;
    const paused = this.#stopped;
    this.#toggle.setAttribute('aria-label', paused ? this.#toggle.dataset.labelPlay : this.#toggle.dataset.labelPause);
    this.#toggle.toggleAttribute('data-paused', paused);
  }
}

if (!customElements.get('s-hero-fade')) customElements.define('s-hero-fade', SHeroFade);
